import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useCustomDispatch } from '../hooks';
import { useCustomSelectors } from '../services/selectors/allSelectors';
import { fetchUserProfile } from '../services/actions/auth/userProfileActions';
import { authToken } from '../storage/authToken';
import { subscribeBirthdayEvents } from '../utils/miscellaneous/ably';
import { safeToast } from '../utils/helper/helper';
import {
	deferBirthdayDobPrompt,
	fetchBirthdayContext,
	fetchBirthdayQuote,
	sendBirthdayWish,
	updateBirthdayDob
} from '../services/actions/auth/birthdayApi';

const BirthdayContext = createContext(null);

export const BirthdayProvider = ({ children }) => {
	const dispatch = useCustomDispatch();
	const token = authToken();
	const { profileData, pharmacyId } = useCustomSelectors();
	const [birthdayData, setBirthdayData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const birthdayDataRef = useRef(null);
	const handledWishEventKeysRef = useRef(new Set());

	const refreshBirthdayData = useCallback(async ({ silent = false } = {}) => {
		if (!token || !profileData?.user_id || !pharmacyId) {
			setBirthdayData(null);
			return null;
		}

		if (!silent) {
			setLoading(true);
		}

		try {
			const response = await fetchBirthdayContext();
			setBirthdayData(response?.data?.data ?? null);
			setError(null);
			return response?.data?.data ?? null;
		} catch (err) {
			setError(err);
			return null;
		} finally {
			if (!silent) {
				setLoading(false);
			}
		}
	}, [pharmacyId, profileData?.user_id, token]);

	useEffect(() => {
		if (!token || !profileData?.user_id || !pharmacyId) {
			setBirthdayData(null);
			setError(null);
			return;
		}

		refreshBirthdayData({ silent: false });
	}, [pharmacyId, profileData?.user_id, refreshBirthdayData, token]);

	useEffect(() => {
		birthdayDataRef.current = birthdayData;
	}, [birthdayData]);

	useEffect(() => {
		if (!token || !profileData?.user_id || !pharmacyId) {
			return undefined;
		}

		const subscription = subscribeBirthdayEvents(pharmacyId, async (eventData) => {
			const currentUserId = Number(profileData?.user_id);
			const currentBirthdayUsers = birthdayDataRef.current?.today_birthdays || [];
			const currentBirthdayUserIds = currentBirthdayUsers.map((user) => Number(user?.user_id)).filter(Boolean);

			const shouldRefresh = (() => {
				switch (eventData?.event) {
					case 'birthday-day-sync':
						return true;
					case 'birthday-dob-updated':
						return currentUserId === Number(eventData?.user_id)
							|| eventData?.is_birthday_today === true
							|| currentBirthdayUserIds.includes(Number(eventData?.user_id));
					case 'birthday-dob-reminder-deferred':
						return currentUserId === Number(eventData?.target_user_id);
					case 'birthday-wish-sent':
						return currentUserId === Number(eventData?.recipient_user_id)
							|| currentBirthdayUserIds.includes(Number(eventData?.recipient_user_id));
					default:
						return false;
				}
			})();

			if (!shouldRefresh) {
				return;
			}

			if (
				eventData?.event === 'birthday-wish-sent'
				&& currentUserId === Number(eventData?.recipient_user_id)
				&& currentUserId !== Number(eventData?.sender_user_id)
			) {
				const eventKey = [
					eventData?.event,
					eventData?.recipient_user_id,
					eventData?.sender_user_id,
					eventData?.date,
					eventData?.timestamp,
				].join(':');

				if (!handledWishEventKeysRef.current.has(eventKey)) {
					handledWishEventKeysRef.current.add(eventKey);
					if (handledWishEventKeysRef.current.size > 50) {
						const oldestKey = handledWishEventKeysRef.current.values().next().value;
						handledWishEventKeysRef.current.delete(oldestKey);
					}
					safeToast.info(`${eventData?.sender_name || 'A teammate'} sent you a birthday wish.`);
				}
			}

			if (eventData?.event === 'birthday-dob-updated' && currentUserId === Number(eventData?.user_id)) {
				await dispatch(fetchUserProfile());
			}

			await refreshBirthdayData({ silent: true });
		});

		return () => {
			if (subscription?.close) {
				subscription.close();
			}
		};
	}, [dispatch, pharmacyId, profileData?.user_id, refreshBirthdayData, token]);

	const saveDob = useCallback(async (dob) => {
		try {
			const response = await updateBirthdayDob(dob);
			await dispatch(fetchUserProfile());
			await refreshBirthdayData({ silent: true });
			safeToast.success(response?.data?.message || 'Date of birth updated.');
			return response?.data;
		} catch (error) {
			safeToast.error(error?.response?.data?.message || 'Unable to update date of birth.');
			throw error;
		}
	}, [dispatch, refreshBirthdayData]);

	const deferDobPrompt = useCallback(async (days) => {
		try {
			const response = await deferBirthdayDobPrompt(days);
			await refreshBirthdayData({ silent: true });
			safeToast.success(response?.data?.message || 'Birthday reminder updated.');
			return response?.data;
		} catch (error) {
			safeToast.error(error?.response?.data?.message || 'Unable to update the reminder.');
			throw error;
		}
	}, [refreshBirthdayData]);

	const getBirthdayQuote = useCallback(async (userId) => {
		const response = await fetchBirthdayQuote(userId);
		return response?.data?.data?.quote || '';
	}, []);

	const submitBirthdayWish = useCallback(async ({ recipientUserId, message }) => {
		try {
			const response = await sendBirthdayWish({ recipientUserId, message });
			await refreshBirthdayData({ silent: true });
			safeToast.success(response?.data?.message || 'Birthday wish sent.');
			return response?.data?.data || null;
		} catch (error) {
			safeToast.error(error?.response?.data?.message || 'Unable to send birthday wish.');
			throw error;
		}
	}, [refreshBirthdayData]);

	const value = useMemo(() => ({
		birthdayData,
		birthdayLoading: loading,
		birthdayError: error,
		refreshBirthdayData,
		saveDob,
		deferDobPrompt,
		getBirthdayQuote,
		submitBirthdayWish,
	}), [birthdayData, loading, error, refreshBirthdayData, saveDob, deferDobPrompt, getBirthdayQuote, submitBirthdayWish]);

	return (
		<BirthdayContext.Provider value={value}>
			{children}
		</BirthdayContext.Provider>
	);
};

export const useBirthday = () => {
	const context = useContext(BirthdayContext);

	if (!context) {
		throw new Error('useBirthday must be used within BirthdayProvider');
	}

	return context;
};
